import React from "react";
import { Link } from "react-router-dom";
import MetLogo from "../components/site/MetLogo";

const SECTIONS = [
  {
    title: "Préambule",
    body: [
      `Dans cette déclaration de confidentialité, « on », « nous » et « notre » « MET Group » se réfèrent à MET Holding AG et ses filiales consolidées.`,
      `Les entreprises membre de MET Group s'engagent à préserver la confidentialité des données personnelles des visiteurs de leurs sites web avec l'URL met.com (ci-après « site web »).`,
      `Veuillez lire attentivement la déclaration de confidentialité qui suit afin de comprendre comment toute information personnelle identifiable et toute information non-personnelle identifiable que vous fournissez en visitant les sites web seront traitées. Les entreprises membres de MET Group en qualité de responsable de traitement collectent et traitent vos données personnelles selon les finalités et les bases légales déterminées ci-après, conformément au RGPD.`,
    ],
  },
  {
    title: "Combien de temps gardons-nous vos données personnelles ?",
    body: [
      `Nous traiterons vos données selon le principe de limitation aussi longtemps qu'il est strictement nécessaire dans le but pour lequel elles sont traitées. S'il n'y a pas d'autre période de rétention spécifique, nous supprimerons vos données une fois que la tâche spécifique est terminée.`,
    ],
  },
  {
    title: "Traitement des informations",
    body: [
      `MET Group ne collecte pas activement et personnellement les informations identifiables des sites Web sauf si elles sont spécifiquement fournies par vous en toute connaissance de cause. Nous utilisons une technologie de traçage (« Cookies ») pour rassembler des données comme le type de recherche et le système opérationnel, les indicateurs, le lieu géographique et la compréhension de la façon dont les visiteurs utilisent les sites Web. Les données enregistrées sont utilisées anonymement.`,
    ],
  },
  {
    title: "Formulaire de contact",
    body: [
      `Vous pouvez nous contacter en remplissant le formulaire de contact. Il vous sera demandé de donner votre nom, adresse email et éventuellement votre numéro de téléphone. Les données personnelles seront traitées dans le but de satisfaire la demande de votre message ou de répondre à votre question, basé sur votre consentement. Nous n'utiliserons pas les informations que vous fournissez avec le formulaire de contact dans un autre but. Vos données seront traitées jusqu'à ce que votre demande soit satisfaite. Si votre message est en relation avec la poursuite d'une réclamation, nous traiterons vos données personnelles jusqu'à la fin de la 5ème année après que les processus connexes aient été finalisés.`,
    ],
  },
  {
    title: "Quelles données sont traitées par nos soins ?",
    body: [
      `Nous traitons tout ou partie des données suivantes : informations sur votre identité (civilité, nom, prénom, adresse postale, email, téléphone, capital de votre entreprise, représentants) ; données de facturation si signature directe sur le site ; données de position du point de livraison ; informations sur votre activité professionnelle ; données de suivi de nos relations commerciales si vous y consentez ; informations en lien avec votre navigation sur le site.`,
    ],
  },
  {
    title: "À quelles fins vos données sont-elles traitées ?",
    body: [
      `Améliorer les performances du site ; gérer les formulaires de prise de contact ; gérer nos opérations de prospection commerciale ; gérer vos demandes de contact ; gérer vos candidatures ; élaborer des statistiques commerciales ; mesurer l'audience de notre site ; gérer les avis des personnes recourant à nos services ; gérer les impayés et le contentieux ; envoyer une demande de devis pour la fourniture d'électricité et/ou de gaz ; signer directement sur le site un contrat de fourniture.`,
    ],
  },
  {
    title: "Quelle est la base juridique du traitement ?",
    body: [
      `L'exécution d'un contrat ; l'exécution de mesures précontractuelles prises à votre demande ; les obligations légales ; les campagnes promotionnelles dans la mesure autorisée par l'utilisateur. Le traitement basé sur votre consentement exprès concerne : l'utilisation de vos données à des fins de prospection commerciale et l'utilisation via les cookies.`,
    ],
  },
  {
    title: "Modalités de traitement et sécurité",
    body: [
      `Les données seront traitées par des moyens informatisés ou automatisés assurant protection, sécurité et confidentialité. MET Group prend des mesures techniques et organisationnelles appropriées pour protéger vos informations d'un accès non autorisé, d'une utilisation inappropriée ou d'une perte accidentelle.`,
    ],
  },
  {
    title: "Vos droits",
    body: "rights",
  },
  {
    title: "Transferts de données",
    body: [
      `Le processeur des données primaires MET Holding AG se trouve en Suisse. Les transferts sont encadrés par la décision adéquate de la Commission Européenne 2000/518/EC. Les transferts intra-groupe sont gouvernés par les Clauses Contractuelles Standards selon la décision UE 2021/914.`,
    ],
  },
  {
    title: "Changements de la déclaration",
    body: [
      `MET Group se réserve le droit de modifier cette déclaration. La date effective de cette déclaration est le 9 août 2023.`,
    ],
  },
];

const Section = ({ title, children }) => (
  <section
    className="mt-8"
    data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
  >
    <h2 className="text-xl md:text-2xl font-extrabold text-[#003242] tracking-tight mb-3">
      {title}
    </h2>
    <div className="text-[#1A2E2C]/80 text-base leading-relaxed font-medium space-y-4">
      {children}
    </div>
  </section>
);

const RightsBlock = () => (
  <p>
    {`Vous pouvez demander : l'accès à vos données personnelles ; la rectification de données inexactes ; l'effacement de vos données ; la restriction du traitement ; de vous opposer au traitement. Le consentement peut être retiré à tout moment. Pour exercer vos droits : `}
    <a className="text-[#005870] font-semibold hover:underline" href="mailto:dataprotection@met.com">
      dataprotection@met.com
    </a>
    {`. Vous pouvez également déposer une plainte auprès de la CNIL : `}
    <a className="text-[#005870] font-semibold hover:underline" href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">
      www.cnil.fr
    </a>
    {`.`}
  </p>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="border-b border-[#003242]/10 bg-white">
        <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-home-link">
            <MetLogo variant="brand" className="h-10 w-10" testid="privacy-logo" />
            <div className="leading-none">
              <div className="text-[#003242] font-extrabold tracking-tight text-base">MET France</div>
              <div className="text-[#005870] text-[10px] font-medium tracking-[0.18em] uppercase mt-1">
                {`L'énergie d'entreprendre`}
              </div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-[#005870] font-bold text-sm hover:underline"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M11 18l-6-6 6-6"/>
            </svg>
            {`Retour à l'accueil`}
          </Link>
        </div>
      </header>

      <main className="max-w-[800px] mx-auto px-6 py-12 md:py-16">
        <h1
          className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-[#003242] leading-tight"
          data-testid="privacy-title"
        >
          Politique de confidentialité
        </h1>
        <p className="mt-3 text-[#005870] font-semibold">MET Energie France</p>
        <p className="mt-1 text-sm text-[#1A2E2C]/60 font-medium">
          Siège social : 4 Rue de la République - 69001 LYON — Capital : 1 000 000,00 € — 917 547 549 Lyon
        </p>

        {SECTIONS.map((s) => (
          <Section key={s.title} title={s.title}>
            {s.body === "rights" ? (
              <RightsBlock />
            ) : (
              s.body.map((p, i) => <p key={i}>{p}</p>)
            )}
          </Section>
        ))}

        <div className="mt-14 pt-8 border-t border-[#003242]/10">
          <Link
            to="/"
            data-testid="privacy-back-bottom"
            className="inline-flex items-center gap-2 text-[#005870] font-bold hover:underline"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M11 18l-6-6 6-6"/>
            </svg>
            {`Retour à l'accueil`}
          </Link>
        </div>
      </main>
    </div>
  );
}
